/**
 * Screen 9 — group selected DeCAs into one multi-consignment document.
 *
 * BOE Sixth permits grouping only where the contractual shipper **and** the
 * actual carrier are the same for every consignment, so the review step names
 * the parties once and refuses to submit a selection that disagrees. The server
 * enforces it too — a mismatch comes back as `422 GROUPING_PARTIES_DIFFER` with
 * the offending documents in `conflicts` — and that response is rendered rather
 * than replaced with our own guess at what went wrong. The per-document
 * `conflicts` array is rendered directly; `apiErrorMessage` is the fallback
 * for a problem that carries none.
 *
 * The result is a **new** document carrying one consignment per source; the
 * sources become `ANULADO / SUPERSEDED` and stay linked to it in both
 * directions. Their direct downloads keep working until their normal expiry
 * (Third.2), which is why this is not framed as a deletion.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { unwrap } from '@deca/api'

import { api } from '../app/api.ts'
import { useAuth } from '../app/auth.tsx'
import { apiErrorMessage, conflictsOf, type GroupConflict } from '../app/errors.ts'
import { useDecasByIds, useInvalidateDeca } from '../app/queries.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { MAX_GROUP_SELECTION, samePartiesAs } from '../deca/estado.ts'
import { formatDateTime } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, EmptyState, LoadingRows, NoAccessState, Notice } from '../ui/primitives.tsx'
import { EstadoChip } from '../ui/EstadoChip.tsx'
import { TextAreaInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { groupSearchSchema } from './searchSchemas.ts'

export function GroupScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateDeca()

  const rawSearch = useSearch({ strict: false })
  const ids = useMemo(() => groupSearchSchema.parse(rawSearch).ids ?? [], [rawSearch])

  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  // Holds the caught value itself, not a formatted string: `conflictsOf` needs the error
  // object to read `problem.conflicts`, and the plain-message fallback still needs
  // `apiErrorMessage` at render time, so neither can be derived ahead and thrown away.
  const [failure, setFailure] = useState<unknown>(null)

  // Over the contract's cap, refuse before fetching anything: `useDecasByIds([])` fires no
  // requests at all, rather than the fan-out `ids` would otherwise produce.
  const overCap = ids.length > MAX_GROUP_SELECTION

  // The selection came through the URL, so the documents are re-read rather than carried in
  // memory — a pasted or reloaded link has to work. Read one by one, not a page of the list:
  // a selection made across pages is not a subset of any single page, which is how an id used
  // to reach the request without ever appearing in the review table below or in the party
  // pre-check that reads `rows`.
  const results = useDecasByIds(overCap ? [] : ids)
  const isLoading = results.length > 0 && results.some((result) => result.isLoading)
  // Index-aligned with `ids` (both come from mapping over the same array in
  // `useDecasByIds`/`useDecaList`), so the failing id is recoverable — the notice below names
  // it rather than collapsing every failure into one sentence with no way to tell which
  // document is missing.
  const loadFailures = useMemo(
    () =>
      results
        .map((result, index) => ({ id: ids[index]!, result }))
        .filter((entry) => entry.result.isError),
    [results, ids],
  )
  // Every read must currently be `isSuccess`, not merely "has data": TanStack Query keeps the
  // last-good `data` around when a background refetch fails, so a query can be `isError` while
  // still holding a stale row — `rows.length === ids.length` would count it as loaded and let
  // a document whose current read failed slip back into the review table and the party
  // pre-check below. `staleTime` is 30s (`main.tsx`), so this is not a corner case: a document
  // opened elsewhere more than half a minute earlier is served from cache and refetched on
  // mount here, and any refetch failure — a colleague voiding it, a 5xx — reproduces it.
  const everyReadSucceeded = results.length > 0 && results.every((result) => result.isSuccess)
  const rows = useMemo(
    () =>
      results
        .map((result) => (result.isSuccess ? result.data.data : undefined))
        .filter((row): row is NonNullable<typeof row> => row != null),
    [results],
  )

  // `samePartiesAs`, not a raw comparison of the two NIFs: the server compares them normalised
  // (`backend/src/deca/grouping.ts`'s `partiesDiffer`, via `deca/identifiers.ts`), so a raw
  // check here disabled submit on a selection the server would have grouped — `A-41118829` and
  // `A41118829` are one legal person and both are storable. One shared function with
  // `canGroupTogether`, which gates the Agrupar button on the list: the two are the same rule at
  // two moments, and they were written twice and drifted together.
  const partiesMatch = useMemo(() => {
    const first = rows[0]
    return first == null || rows.every((row) => samePartiesAs(row, first))
  }, [rows])

  // The one place that decides whether a group may be submitted — read by the button's
  // `disabled` *and* by `submit()` itself, so the two cannot drift the way `allLoaded` and the
  // handler once did (the handler carried no check of its own at all). A disabled button is a
  // UI affordance, not a guard.
  const canSubmit = everyReadSucceeded && partiesMatch && rows.length >= 2

  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  // The same in-component check `DecaCreateScreen` and `ImportScreen` make. Surface-level
  // gating is the route (`DecaSurface`, on `handlesDecas`); an ability *within* the surface is
  // checked here, and `ORG_VIEWER` has the former without the latter.
  if (!abilities.writeDecas) {
    return (
      <AppShell title={t('group.title')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  async function submit() {
    // Re-checked here, not only via the button's `disabled`: that is what disappeared without
    // being missed when `allLoaded` went wrong, because the only path exercised in practice was
    // the click on an (assumed-correct) disabled button. `canSubmit` is the same expression the
    // button reads, so the two cannot disagree — but the request must never go out because a
    // click happened to reach this function, only because the reviewed set is actually ready.
    if (busy || !canSubmit) return
    setBusy(true)
    setFailure(null)
    try {
      const created = await unwrap(
        api.POST('/api/v1/decas/group', {
          body: {
            documentIds: ids,
            generate: true,
            ...(reason.trim() ? { reason: reason.trim() } : {}),
          },
        }),
      )
      for (const id of ids) invalidate(id)
      invalidate(created.id)
      toast.success(t('group.grouped', { documentNumber: created.documentNumber ?? '' }))
      void navigate({ to: '/decas/$decaId', params: { decaId: created.id } })
    } catch (caught) {
      setFailure(caught)
      // Read the selection again after a refusal about its documents — above all one that moved
      // while it was being grouped (#110's review): a table still showing what was checked would
      // have the operator review, and resend, a picture the server no longer holds. Not after any
      // other failure: fifty reads into the same outage would empty the table and disable Agrupar
      // until a reload, which also loses the typed reason.
      if (conflictsOf(caught)) for (const id of ids) invalidate(id)
    } finally {
      setBusy(false)
    }
  }

  const conflicts = conflictsOf(failure)

  return (
    <AppShell title={t('group.title')} subtitle={t('group.subtitle')}>
      <Notice tone="info">{t('group.rule')}</Notice>

      {overCap ? (
        <Card>
          <EmptyState
            icon="group"
            title={t('group.tooManyTitle')}
            body={t('group.tooManyBody', { max: MAX_GROUP_SELECTION })}
            action={
              <Button variant="primary" onClick={() => void navigate({ to: '/decas', search: {} })}>
                {t('list.title')}
              </Button>
            }
          />
        </Card>
      ) : ids.length < 2 ? (
        <Card>
          <EmptyState
            icon="group"
            title={t('group.needTwo')}
            body={t('list.groupHint')}
            action={
              <Button variant="primary" onClick={() => void navigate({ to: '/decas', search: {} })}>
                {t('list.title')}
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <GroupNotices
            conflicts={conflicts}
            failure={failure}
            partiesMatch={partiesMatch}
            loadFailures={loadFailures}
            t={t}
          />

          {/* Advisory, not a reaction to a failure — unlike every branch of `GroupNotices`
              above — so it lives outside that component and is not gated on `canSubmit`:
              it is true of what submitting *will* do even while the selection is still
              being fixed, the same way `group.rule` states a fact rather than a warning. */}
          <Notice tone="info">{t('group.seventhNotice')}</Notice>

          <Card title={t('group.selected')} flush>
            {isLoading ? (
              <LoadingRows rows={ids.length} columns={4} />
            ) : (
              <div className="table-wrap">
                {/* `table--stack`: on a phone each row becomes a block and each cell prints its
                    `data-label` above its value (#111) — one DOM, CSS only. */}
                <table className="table table--stack">
                  <caption className="sr-only">{t('group.selected')}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('list.columns.documentNumber')}</th>
                      <th scope="col">{t('list.columns.cargador')}</th>
                      <th scope="col">{t('list.columns.transportista')}</th>
                      <th scope="col">{t('list.columns.route')}</th>
                      <th scope="col">{t('list.columns.fecha')}</th>
                      <th scope="col">{t('list.columns.estado')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td data-label={t('list.columns.documentNumber')}>
                          <span className="doc-number">{row.documentNumber}</span>
                        </td>
                        <td data-label={t('list.columns.cargador')}>
                          {row.cargador_razon_social}
                          <div className="small muted tabular">{row.cargador_nif}</div>
                        </td>
                        <td data-label={t('list.columns.transportista')}>
                          {row.transportista_razon_social}
                          <div className="small muted tabular">{row.transportista_nif}</div>
                        </td>
                        <td data-label={t('list.columns.route')}>
                          {t('list.routeSummary', {
                            // Not `origen_resumen`/`destino_resumen`: those are `DecaSummary`
                            // fields computed by the list endpoint. A per-id read returns the
                            // full `Deca`, whose consignments carry their own addresses instead.
                            // Only the first consignment is shown — a portal-side simplification,
                            // not a guarantee. An already-grouped document can be selected here
                            // (nothing before submission stops it) and would show only its first
                            // leg's route until the server's own `ALREADY_GROUPED` refusal comes
                            // back; multi-leg display is not implemented on this review table.
                            origen: row.consignments[0]?.origen_domicilio ?? '',
                            destino: row.consignments[0]?.destino_domicilio ?? '',
                          })}
                        </td>
                        <td className="tabular" data-label={t('list.columns.fecha')}>
                          {formatDateTime(row.fecha_realizacion, i18n.language)}
                        </td>
                        <td data-label={t('list.columns.estado')}>
                          <EstadoChip estado={row.estado} voidReason={row.voidReason} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <div className="stack">
              <TextAreaInput
                label={t('group.reason')}
                rows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <p className="small muted">{t('group.orderMeaning')}</p>
              {/* `wrap`: the two nowrap buttons are wider than a 375 px card, and an unwrapped
                  row pushed the whole page sideways (#111). */}
              <div className="row wrap">
                <Button onClick={() => void navigate({ to: '/decas', search: {} })} disabled={busy}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  icon="group"
                  loading={busy}
                  disabled={busy || !canSubmit}
                  onClick={() => void submit()}
                >
                  {t('group.submit')}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </AppShell>
  )
}

/**
 * Every *reactive* pre-submit notice this screen can show, in one place: the server's 422
 * `conflicts` or a plain submit failure, the party pre-check, and the per-document load
 * failures. Each branch answers something that has gone, or might go, wrong.
 *
 * The BOE Seventh notice (`GroupScreen`, above the review table) is deliberately not a fifth
 * branch here: it states what a successful submit *will* do, not a reaction to a failure, so
 * it has to stay visible even while every branch above is silent — or while one of them is
 * showing, such as a party mismatch the user is still fixing.
 */
function GroupNotices({
  conflicts,
  failure,
  partiesMatch,
  loadFailures,
  t,
}: {
  conflicts: GroupConflict[] | null
  failure: unknown
  partiesMatch: boolean
  loadFailures: ReadonlyArray<{ id: string; result: { error: unknown } }>
  t: TFunction
}) {
  return (
    <>
      {conflicts ? (
        <Notice
          tone="danger"
          title={
            // Nothing is wrong with a source that moved: lead with what to do, not "cannot be grouped".
            conflicts.every((c) => c.reason === 'SOURCE_CHANGED') ? apiErrorMessage(failure, t) : t('group.conflictsTitle')
          }
        >
          <ul className="stack-sm" style={{ margin: 0, paddingLeft: 'var(--space-lg)' }}>
            {conflicts.map((c) => {
              // `defaultValue: ''`, the same idiom `apiErrorMessage` uses (`errors.ts:19`): a
              // `reason` the portal does not yet have a label for — a server-side addition
              // ahead of a portal release — must not print the raw i18next key on screen. The
              // server's own `detail` already carries the meaning, so dropping the label reads
              // fine rather than confusingly.
              const label = t(`group.conflict.${c.reason}`, { defaultValue: '' })
              const number = c.documentNumber ?? c.documentId
              return (
                <li key={c.documentId} className="small">
                  {label ? `${number} — ${label}: ${c.detail}` : `${number}: ${c.detail}`}
                </li>
              )
            })}
          </ul>
        </Notice>
      ) : failure ? (
        <Notice tone="danger">{apiErrorMessage(failure, t)}</Notice>
      ) : null}
      {/* The portal's own pre-check over the rows already loaded, fired before a request
          is made — a different moment from the server's `conflicts` above, and left alone
          so the request-stopping check keeps its value. */}
      {!partiesMatch ? <Notice tone="danger">{t('group.partiesDiffer')}</Notice> : null}
      {loadFailures.length > 0 ? (
        // A fan-out of up to 50 single reads means *some* succeeding while others fail (a
        // stale id, a 404, a document a colleague just voided) is the ordinary case, not the
        // exception — so every failing id is named, the same way the `conflicts` list above
        // names every offending document rather than reporting only the first. Submit stays
        // blocked (`canSubmit`) until every one of them has succeeded.
        <Notice tone="danger" title={t('group.loadErrorsTitle')}>
          <ul className="stack-sm" style={{ margin: 0, paddingLeft: 'var(--space-lg)' }}>
            {loadFailures.map(({ id, result }) => (
              <li key={id} className="small">
                {id}: {apiErrorMessage(result.error, t)}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </>
  )
}
